import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import {
  createSettingItem,
  deleteSettingItem,
  fetchSettings,
  selectSettingsGroup,
  updateSettingItem,
} from '@main/features/settings/settingsSlice';
import { UiButton, UiCheckbox, UiInput, UiNativeSelect, UiTextarea } from '@shared/common/ui';

const SYSTEM_TYPE_OPTIONS = [
  { value: '', label: '— не задан —' },
  { value: 'lead', label: 'lead — первичный / в обработке' },
  { value: 'trial', label: 'trial — пробное' },
  { value: 'active', label: 'active — занимается' },
  { value: 'paused', label: 'paused — пауза' },
  { value: 'left', label: 'left — ушёл / отток' },
  { value: 'archive', label: 'archive — архив' },
];

/** ТЗ «Дата пробного», §40: поведение статуса при работе с датой пробного занятия. */
const TRIAL_DATE_MODE_OPTIONS = [
  { value: 'none', label: 'Не используется' },
  { value: 'set_required', label: 'Требовать дату пробного' },
  { value: 'reschedule_required', label: 'Требовать новую дату при переносе' },
];

const TRIAL_DATE_MODE_SHORT = {
  set_required: 'дата пробного',
  reschedule_required: 'перенос пробного',
};

const DEFAULT_FORM = {
  name: '',
  color: '#3a6df0',
  description: '',
  system_type: '',
  trial_date_mode: 'none',
  is_default: false,
  is_close_default: false,
  is_active: true,
};

/**
 * Справочник статусов клиентов с system_type / default / close-default (ТЗ v2).
 */
export function ClientStatusesSettings() {
  const dispatch = useDispatch();
  const group = 'client_statuses';
  const { items, status, saveStatus, saveError } = useSelector(selectSettingsGroup(group));
  const [editingKey, setEditingKey] = useState(null);
  // Настройки воронки живут на отдельном экране — здесь их только переносим без изменений.
  const [funnelFields, setFunnelFields] = useState({});

  useEffect(() => {
    if (status === 'idle') dispatch(fetchSettings(group));
  }, [dispatch, status]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: DEFAULT_FORM });

  const onSubmit = async (values) => {
    const value = {
      name: values.name.trim(),
      color: values.color,
      description: values.description?.trim() || null,
      system_type: values.system_type || null,
      trial_date_mode: values.trial_date_mode || 'none',
      is_default: Boolean(values.is_default),
      is_close_default: Boolean(values.is_close_default),
      is_active: values.is_active !== false,
      ...funnelFields,
    };
    const thunk = editingKey
      ? updateSettingItem({ group, key: editingKey, value })
      : createSettingItem({ group, value });
    const result = await dispatch(thunk);
    if (result.meta.requestStatus === 'fulfilled') {
      reset(DEFAULT_FORM);
      setEditingKey(null);
      setFunnelFields({});
    }
  };

  const onEdit = (item) => {
    setEditingKey(item.key);
    setFunnelFields({
      is_funnel_stage: Boolean(item.value?.is_funnel_stage),
      funnel_order: item.value?.funnel_order ?? null,
    });
    reset({
      name: item.value?.name ?? '',
      color: item.value?.color ?? '#3a6df0',
      description: item.value?.description ?? '',
      system_type: item.value?.system_type ?? item.value?.crm_logic ?? '',
      trial_date_mode: item.value?.trial_date_mode ?? 'none',
      is_default: Boolean(item.value?.is_default),
      is_close_default: Boolean(item.value?.is_close_default),
      is_active: item.value?.is_active !== false,
    });
  };

  const onCancel = () => {
    setEditingKey(null);
    setFunnelFields({});
    reset(DEFAULT_FORM);
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Архивировать статус «${item.value?.name ?? item.key}»?`)) return;
    await dispatch(deleteSettingItem({ group, key: item.key }));
  };

  return (
    <div className="form-grid" style={{ alignItems: 'start' }}>
      <div className="form-card">
        <h2>{editingKey ? 'Редактирование статуса' : 'Добавить статус'}</h2>
        <p className="muted">
          Системный тип нужен для логики CRM (воронка, summary, отток). Название можно менять.
        </p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <UiInput
            label="Название"
            type="text"
            error={errors.name?.message}
            {...register('name', {
              required: 'Введите название',
              maxLength: { value: 255, message: 'Слишком длинное название' },
            })}
          />
          <UiInput label="Цвет" type="color" style={{ maxWidth: 120 }} {...register('color')} />
          <UiTextarea
            label="Описание"
            rows={2}
            {...register('description', {
              maxLength: { value: 500, message: 'Не больше 500 символов' },
            })}
          />
          <UiNativeSelect label="Системный тип" {...register('system_type')}>
            {SYSTEM_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value || 'empty'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </UiNativeSelect>

          <UiNativeSelect label="Работа с датой пробного" {...register('trial_date_mode')}>
            {TRIAL_DATE_MODE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </UiNativeSelect>
          <p className="muted" style={{ marginTop: 4 }}>
            Если статус требует дату, CRM спросит её до перевода клиента — в карточке и при
            перетаскивании в воронке.
          </p>

          <div style={{ display: 'grid', gap: 8, margin: '12px 0' }}>
            <UiCheckbox
              label="Статус по умолчанию (новая запись на направление)"
              {...register('is_default')}
            />
            <UiCheckbox
              label="Статус закрытия («Прекратить занятия»)"
              {...register('is_close_default', {
                onChange: (e) => {
                  if (e.target.checked && watch('system_type') !== 'left') {
                    setValue('system_type', 'left');
                  }
                },
              })}
            />
            <UiCheckbox label="Активен в справочнике" {...register('is_active')} />
          </div>

          {saveError && <div className="banner banner--danger">{saveError}</div>}

          <div className="form-actions">
            {editingKey && (
              <UiButton variant="ghost" type="button" onClick={onCancel}>
                Отмена
              </UiButton>
            )}
            <UiButton type="submit" disabled={isSubmitting || saveStatus === 'loading'}>
              {editingKey ? 'Сохранить' : 'Добавить'}
            </UiButton>
          </div>
        </form>
      </div>

      <div>
        <h2 style={{ marginTop: 0 }}>Статусы клиентов</h2>
        {items.length === 0 ? (
          <div className="placeholder">Пока пусто</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Тип</th>
                <th>Флаги</th>
                <th style={{ width: 160 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const v = item.value ?? {};
                const inactive = v.is_active === false;
                return (
                  <tr key={item.key} style={inactive ? { opacity: 0.55 } : undefined}>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: 3,
                          background: v.color ?? '#ccc',
                          marginRight: 8,
                        }}
                      />
                      {v.name ?? '—'}
                    </td>
                    <td className="muted">{v.system_type ?? v.crm_logic ?? '—'}</td>
                    <td className="muted">
                      {[
                        v.is_default ? 'default' : null,
                        v.is_close_default ? 'close' : null,
                        TRIAL_DATE_MODE_SHORT[v.trial_date_mode] ?? null,
                        inactive ? 'архив' : null,
                      ]
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </td>
                    <td>
                      <div className="row-actions">
                        <UiButton variant="ghost" size="sm" type="button" onClick={() => onEdit(item)}>
                          Изменить
                        </UiButton>
                        {!inactive && (
                          <UiButton
                            variant="danger"
                            size="sm"
                            type="button"
                            onClick={() => onDelete(item)}
                          >
                            Архив
                          </UiButton>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
